/**
 * Single source of truth for the site's absolute origin.
 *
 * Every absolute URL the crawlers see — canonical, hreflang, og:url, og:image,
 * JSON-LD @id/url and the generated sitemap — must resolve through here.
 *
 * Why a hardcoded fallback rather than env-only:
 * without one, an unset VITE_APP_URL degrades every absolute URL to a relative
 * path — which Google ignores for hreflang and which makes og:image unusable for
 * social crawlers. The env var wins whenever it is set.
 *
 * The fallback is the PRODUCTION origin, so a non-production build that forgets to
 * set VITE_APP_URL would advertise production URLs — canonical tags on the dev site
 * pointing at nvbrokerage.com. That is why `build_test` / `build_prod` in
 * .gitlab-ci.yml declare `environment:` (so the per-environment VITE_* variables
 * resolve) and hard-fail when VITE_APP_URL is empty.
 */

/** Production origin. Apex domain, no `www`, no trailing slash. */
const FALLBACK_SITE_URL = "https://nvbrokerage.com";

function readEnvSiteUrl(): string | undefined {
	// Vite statically replaces `import.meta.env.*` in both the client and the
	// server/prerender bundles. Guarded because this module is also imported by
	// `scripts/generateSitemap.ts`, which runs under plain tsx, where reading a
	// member of the missing env object throws. No optional chaining: vike treats
	// `import.meta.env?.` as a bare `import.meta.env` and replaces it with `null`.
	try {
		const fromVite = import.meta.env.VITE_APP_URL;
		if (fromVite) return fromVite;
	} catch {
		// `import.meta.env` is not available in this context — fall through.
	}

	if (typeof process !== "undefined" && process.env?.VITE_APP_URL) {
		return process.env.VITE_APP_URL;
	}

	return undefined;
}

/** Absolute site origin without a trailing slash, e.g. `https://nvbrokerage.com`. */
export const SITE_URL: string = (readEnvSiteUrl() || FALLBACK_SITE_URL).replace(/\/+$/, "");

/**
 * Turn a root-relative path into an absolute URL.
 * Values that are already absolute are returned untouched.
 */
export function absoluteUrl(pathOrUrl: string): string {
	if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
	return `${SITE_URL}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}
